import {Injectable} from '@angular/core';
import {Group, GroupConfig} from "../../classes/Groups/Group";
import {BehaviorSubject, from, Observable, of, Subject, timer} from "rxjs";
import {HttpServiceService} from "../httpService/http-service.service";
import {HttpHeaders, HttpParams} from "@angular/common/http";
import {ServerConnectionService} from "../serverConnection/server-connection.service";
import {Item} from "../../models/Item";
import {UiAdapterService} from "../uiAdapterService/ui-adapter.service";
import {concatMap, delay, endWith} from "rxjs/operators";
import {isBigEventOptimization} from "@src/configs/view_only";
import {CesiumAdapterService} from "@src/app/services/cesiumAdapter21/cesium-adapter.service";

interface GroupLog{
  group: string,
  type: string,
  isGroup: boolean
}

@Injectable({
  providedIn: 'root'
})
export class GroupService {

  private groupVisibility:any = {};
  private _groupListHiddenOnly:any = [];
  private _groupListVisibleOnly:any = [];
  public _groupList: Group[] = [];
  public _groupListUpdated$: BehaviorSubject<Group[]> = new BehaviorSubject([]);
  private _groupItemMoved$: Subject<Item> = new Subject<Item>();
  private _groupItemExpanded$: BehaviorSubject<Group> = new BehaviorSubject<Group>(null);
  private _groupItemTitleFocused$: BehaviorSubject<Group> = new BehaviorSubject<Group>(null);
  private _addToActionLog$: Subject<GroupLog> = new Subject<GroupLog>();
  private preparingCategoryGroupsCB:{[fl: number]: {[fp: string]: {[title: string]: {items:any}}}} = {};
  //new Map<string, { items: any }>();
  private _groupVisibilityUpdated$ = new Subject<{group: Group, visibility: boolean}>();
  public openedGroups = new Map<string | number, Group>();
  public hiddenGroups = new Map<string | number, Group>();
  private groupRequestBuffer =  new Map<string, any[]>();
  private Yiit = Yiit;
  private _itemsLoadedVal: boolean = false;

  get addedToActionLog$() {
    return this._addToActionLog$.asObservable();
  }

  get onGroupVisibilityUpdated(){
    return this._groupVisibilityUpdated$.asObservable();
  }
  private _itemsLoaded$ = new Subject<boolean>();

  set itemsLoaded$(val:any){
    this._itemsLoadedVal = val;
    this._itemsLoaded$.next(val);
  }

  get itemsLoadedVal(){
    return this._itemsLoadedVal;
  }

  get itemsLoaded$(){
    return this._itemsLoaded$.asObservable();
  }

  get groupItemMoved$(){
    return this._groupItemMoved$.asObservable();
  }

  get groupItemExpanded$(){
    return this._groupItemExpanded$.asObservable();
  }

  get groupItemTitleFocused$(){
    return this._groupItemTitleFocused$.asObservable();
  }

  get groupList() {
    return this._groupList;
  }

  private _addedToGroupList$: Subject<Group> = new Subject<Group>();
  get addedToGroupList$() {
    return this._addedToGroupList$.asObservable();
  }

  get groupList$(): Observable<Group[]> {
    return this._groupListUpdated$.asObservable();
  }

  set groupList(list) {
    this._groupListUpdated$.next(list);
    this._groupList = list;
  }

  constructor(
    private http: HttpServiceService,
    private socket: ServerConnectionService,
    private ui: UiAdapterService,
    private view: CesiumAdapterService,
  ) {
    this.init();
    this.view.setGroupService(this);
  }

  private init() {
    let groupList = window['OBJECT_GROUPS'];
    this.groupList = groupList.map((groupElement) => {
      let group = new Group(groupElement);
      this._addedToGroupList$.next(group);
      return group;
    });

    // this.initEmptyGroup();
    this.initListeners();
  }

  forceHideGroupItemsByGroupId(id:any){
    let group:Group = this.getGroupById(id);
    group['items'].forEach((i)=>{
      if (i instanceof Item){
        i.setVisibility(false);
        console.log('forceHideGroupItemsByGroupId', i);
      }
      if (i instanceof Group){
        this.forceHideGroupItemsByGroupId(i['id']);
      }
    });
    this.setGroupItemsVisibility(group, false);
  }
  forceShowGroupItemsByGroupId(id:any){
    let group:Group = this.getGroupById(id);
    group['items'].forEach((i)=>{
      if (i instanceof Item){
        i.setVisibility(true);
        console.log('forceShowGroupItemsByGroupId', i);
      }
      if (i instanceof Group){
        this.forceShowGroupItemsByGroupId(i['id']);
      }
    });
    this.setGroupItemsVisibility(group, true);
  }
  forceShowGroupItems(){
    for(let i in this._groupList){
      this.forceShowGroupItemsByGroupId(this._groupList[i]['id']);
    }
  }

  private initListeners() {
    this.socket.onAction('createGroup', (group) => {
      let _group = new Group(group);
      this.addGroup(_group);
    });
    this.socket.onAction('updateGroup', (group: GroupConfig) => {
      this.updateGroupInternal(group);
    });
    this.socket.onAction('removeGroup', (group) => {
      this._removeGroup(new Group(group));
    });

    this._itemsLoaded$.subscribe((val)=>{
      if (val){
        if (isBigEventOptimization) {
          timer(7000).subscribe(() => {
            this._groupList = this._groupList.filter((g) => {
              return !g.is_empty;
            });
            this._groupListUpdated$.next(this._groupList);
          });
        } else {
          this._groupList = this._groupList.filter((g) => {
            return !g.is_empty;
          });
          this._groupListUpdated$.next(this._groupList);
        }
      }
    })
  }

  get getHiddenGroups() {
    return this._groupListHiddenOnly;
  }
  get getVisibleGroups() {
    return this._groupListVisibleOnly;
  }

  private _visibleItemsList: BehaviorSubject<object> = new BehaviorSubject<object>({});
  get visibleItemsList():Observable<any>{
    return this._visibleItemsList.asObservable();
  }

  setGroupItemsVisibility(group: Group, value: boolean) {
    if (value === false) {
      this.hiddenGroups.set(group.getId(), group);
    } else {
      this.hiddenGroups.delete(group.getId());
    }

    // this._groupVisibilityUpdated$.next({group: group, visibility: value});

    let res = {group: {id:group['id']}, visibility: value};
    this.groupVisibility[group['id']] = res;
    this._groupListHiddenOnly = [];
    for(let i in this.groupVisibility){
      if(!this.groupVisibility[i].visibility){
        this._groupListHiddenOnly.push(this.groupVisibility[i].group.id);
        let list:any = this._visibleItemsList.getValue();
        for(let i in list){
          let item:Item = list[i];
          let itemGroupId:any = item.config['groupId'];
          if(itemGroupId == group.getId()){
            delete list[item.getId()];
            //list.splice(i, 1);
            // list.removeItem(item);
          }
        }
        this._visibleItemsList.next(list);
      }else{
        this._groupListVisibleOnly.push(this.groupVisibility[i].group.id);
        let list:any = this._visibleItemsList.getValue();
        for(let i in group.items){
          let item:Item = group.items[i];
          list[item.getId()] = item;
        }
        this._visibleItemsList.next(list);
      }
    }
    this._groupVisibilityUpdated$.next({group: group, visibility: value});
  }

  public initEmptyGroup(cb) {
    if (!this.getGroupById(null)) {
      let config = {
        title: 'Other',
        parent_id: null,
        from_object_category_id: null,
        position: 999999,
        for_tools: null
      };
      this.createGroup(config, cb);
    }
  }

  addGroup(config) {
    if (config.hasOwnProperty('id') && config.id === null) {
      config.id = -1;
    }

    let group = (config instanceof Group) ? config : new Group(config);
    if (this.groupList.indexOf(group) < 0) {
      this._groupList.push(group);
      this._addedToGroupList$.next(group);
      if (group.getParentGroupId()){
        this._groupItemExpanded$.next(this.getGroupById(group.getParentGroupId()));
      }
    }

    this._groupListUpdated$.next(this._groupList);
    return group;
  }

  addSubgroup(currentGroup) {
    let preparedConfig = {
      title: 'New Sub Folder',
      parent_id: currentGroup.getId(),
      floorplan_id: currentGroup.getFloorPlanId(),
      position: 100,
      was_changed: true
    };
    this.createGroup(preparedConfig);
  }


  private getGroupNameIndex(group){
    let sameNameArr = this.groupList.filter((i)=>{
      return i.getTitle().indexOf(group.getTitle()) >= 0 && group.getParentGroupId() == i.getParentGroupId() && group.getFloorPlanId() == i.getFloorPlanId();
    });

    let isContainGroupWithoutIndex = false;
    for (let g of sameNameArr){
      if (g.getTitle().toString().toLowerCase() == group.getTitle().toString().toLowerCase()){
        isContainGroupWithoutIndex = true;
        break;
      }
    }

    if (!isContainGroupWithoutIndex){
      return null;
    }

    let nameCounterArr = sameNameArr.map((g)=>{
      let tmpIndexArr = g.getTitle().split(' ');
      return tmpIndexArr[tmpIndexArr.length-1]
    });

    if (nameCounterArr.length === 0){
      return null;
    }

    let max = (nameCounterArr[0].trim() == '' || isNaN(parseFloat(nameCounterArr[0].trim()))) ? 2 : parseFloat(nameCounterArr[0]);
    let changed = false;
    nameCounterArr.shift();
    for (let i = 0; i < nameCounterArr.length; i++){
      let elm = nameCounterArr[i];
      if (elm.trim() == '' || isNaN(parseFloat(elm))){
        continue;
      }

      if (max <= parseFloat(elm)){
        max = parseFloat(elm);
        changed = true;
      }
    }
    if (changed){
      max+=1;
    }
    return max;
  }

  createGroupForFastLine(config?, cb?){
    let group:Group = new Group((config) ? config : {title: 'New Folder'});

    let _index = this.getGroupNameIndex(group);
    if (_index){
      group.setTitle(group.getTitle()+' '+_index);
    }
    let tmpObj = group.asSimpleObject();
    let groupObjHash = JSON.stringify(tmpObj);
    if (this.groupRequestBuffer.has(groupObjHash)){
      if (cb){
        let currentArr = this.groupRequestBuffer.get(groupObjHash)
        currentArr.push(cb);
        this.groupRequestBuffer.set(groupObjHash, currentArr);
      }
    } else {

      this.groupRequestBuffer.set(groupObjHash, []);
      group.setId(this._groupList.length+1);
      this.addGroup(group);
      // if(!isCategoryGroup && cb){
      if (cb) {
        cb(group);
      }
      this.groupRequestBuffer.get(groupObjHash).forEach((_cb) => {
        _cb(group);
      })
    }
  }

  createGroup(config?, cb?, isCategoryGroup?) {
    let group:Group = null;
      group = new Group((config) ? config : {title: 'New Folder'});
      let configCopy = null;
      if (config){
        configCopy =  Object.assign({}, config);
      }

    let _index = this.getGroupNameIndex(group);
    if (_index){
      group.setTitle(group.getTitle()+' '+_index);
    }
    let newParams = new HttpParams();
    let tmpObj = group.asSimpleObject();
    let groupObjHash = JSON.stringify(tmpObj);
    if (this.groupRequestBuffer.has(groupObjHash)){
      if (cb){
        let currentArr = this.groupRequestBuffer.get(groupObjHash)
        currentArr.push(cb);
        this.groupRequestBuffer.set(groupObjHash, currentArr);
      }
    } else {
      for (let i in tmpObj) {
        newParams = newParams.set(i, (tmpObj[i]) ? tmpObj[i] : '');
      }
      this.groupRequestBuffer.set(groupObjHash, []);
      this.http.sendPostRequest('/event-groups-api/create/' + window['EVENT_ID'], newParams.toString(), new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'
      })).subscribe((result) => {
        try {
          result = JSON.parse(result);
        } catch (e) {
          // console.error('can\'t parse json', e);
        }
        group.setId(result.id);


        if (!config || (configCopy && configCopy.title == this.Yiit.t('New Folder'))) {
          group.was_changed = true;
        }
        this.socket.createGroup(group.asSimpleObject());
        this.addGroup(group);
        // if(!isCategoryGroup && cb){
        if (cb) {
          cb(group);
        }

        //scroll to bottom left sidebar content
        if (!config) {
          this._groupItemExpanded$.next(group);
          this._groupItemTitleFocused$.next(group);

          timer(600).subscribe(() => {
            let scrollbar = Scrollbar.get(document.querySelector('.es-navbar-list-inner-wrapper'));
            scrollbar.scrollTo(0, scrollbar.limit.y, 100);
          });
        }
        this.groupRequestBuffer.get(groupObjHash).forEach((_cb)=>{
          _cb(group);
        })
      });
    }
  };

  private updateGroupInternal(config){
    let group = this.getGroupById(config.id);
    group.updateGroupParams(config);
  }

  updateGroup(group, prevGroup?) {
    let newParams = new HttpParams();
    const tmpObj = group.asSimpleObject();
    for (let i in tmpObj) {
      newParams = newParams.set(i, (tmpObj[i]) ? tmpObj[i] : '');
    }
    this.http.sendPostRequest(
      '/event-groups-api/update/' + window['EVENT_ID'],
      newParams.toString(),
      new HttpHeaders({'Content-Type': 'application/x-www-form-urlencoded'})
    ).subscribe((result) => {
      if (prevGroup) {
        const groupObjHash = this.prepareGroupHash(prevGroup);
        if (this.groupRequestBuffer.has(groupObjHash)) {
          const currentArr = this.groupRequestBuffer.get(groupObjHash);
          const newGroupObjHash = this.prepareGroupHash(group);
          this.groupRequestBuffer.set(newGroupObjHash, currentArr);
          this.groupRequestBuffer.delete(groupObjHash);
        }
      }
      this.socket.updateGroup(group.asSimpleObject());
      this._groupListUpdated$.next(this.groupList);
    });
  }

  private prepareGroupHash(group): string {
    let groupObj;
    if (group.parent_id) {
      groupObj = (new Group({
        title: group.title,
        parent_id: group.parent_id,
        floorplan_id: group.floorplan_id,
        position: group.position,
        was_changed: group.was_changed
      })).asSimpleObject();

    } else {
      groupObj = (new Group({
        title: group.title,
        floorplan_id: group.floorplan_id,
      })).asSimpleObject();

    }
    return JSON.stringify(groupObj);
  }

  private removeGroupItems(group) {
    return new Observable((observer) => {
      from(group.items).pipe(concatMap((item, index) => {
        return of({item, index}).pipe(delay(100))
      })).pipe(endWith({item: null, index: null})).subscribe(({item, index}) => {
        if (item instanceof Item) {
          item.removeObject(false, {isGroupListener: true, groupConfig: group.asJSON()});
          group.items.splice(index, 1);
        }
        if (!item && !index) {
          observer.next(true);
          observer.complete();
        }
      });

      let children = this.getChildGroups(group);
      children.forEach((_group) => {
        this.removeGroup(_group, true);
      });
    })
  }

  private getChildGroups(group) {
    return this._groupList.filter((_group) => {
      return _group.getParentGroupId() == group.getId();
    })
  }

  private isContainId(id){
    return this.groupList.some((group)=>{return group.getId() == id});
  }

  private removeFromListById(id){
    let index = null;
    for (let i = 0; i < this.groupList.length; i++){
      if (this.groupList[i].getId() == id){ index = i; break;}
    }
    if (index!=null){
      this._groupList.splice(index, 1);
    }
  }

  private async _removeGroup(group: Group, isSubgroup?) {
    const groupObjHash = this.prepareGroupHash(group);
    if (this.groupRequestBuffer.has(groupObjHash)) {
      this.groupRequestBuffer.delete(groupObjHash);
    }
    if (group && this.isContainId(group.getId())) {
      let resultGroup = await this.fillGroupsWithSubgroups(group);
      let config =  this.getGroupConfig(resultGroup);

      if (!isSubgroup){
        this._addToActionLog$.next({group: config, type: 'remove', isGroup: true});
      }
      this.removeGroupItems(group).subscribe(() => {
        this.removeFromListById(group.getId());
        this._groupListUpdated$.next(this._groupList);
      });
    }
  }

  private getGroupConfig(group, cb?){
    if (group.subgroups){
      group.subgroups = group.subgroups.map((_subgroup)=>{
        return this.getGroupConfig(_subgroup, cb)
      });
    }
    return group.asJSON(true);
  }

  private async fillSubgroups(group: Group, cb, mainGroup?){
    if (!mainGroup){
      mainGroup = group;
    }
    group.getChildren().subscribe((result) => {
      group.subgroups = result.filter(i => i instanceof Group);
      if (group.subgroups.length == 0){
        return cb(mainGroup);
      }
      group.subgroups.forEach((_subgroup) => {
        this.fillSubgroups(_subgroup, cb, mainGroup);
      });
    });
  }

  public async fillGroupsWithSubgroups(group:Group){
    return await new Promise((resolve) => {
      this.fillSubgroups(group, resolve)
    })
  }

  removeGroup(group, isSubgroup?) {
    let newParams = new HttpParams();
    newParams = newParams.set('id', group.getId());
    this.http.sendPostRequest('/event-groups-api/delete/' + group.getEventId(), newParams.toString(), new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    })).subscribe((result) => {
      this._removeGroup(group, isSubgroup);
      if (!isSubgroup){
        this.socket.removeGroup(group.asSimpleObject());
      }
    });
  }

  public getGroupByCategoryName(category, floorPlanId): Group {

    let searchList = null;
      searchList = this.groupList;

    for (let elm of searchList) {
      //todo: fix group naming
      //category.name == elm.getTitle() &&

      if (floorPlanId){
        floorPlanId = +floorPlanId;
      }

      if ( elm.getTitle().toString().toLowerCase() === category.name.toString().toLowerCase() && elm.getFloorPlanId() == floorPlanId) {
        return elm;
      }
    }
    return null;
  }

  public getGroupById(id: number | string): Group {
    if (id === null) {
      id = -1;
    }
    for (let elm of this.groupList) {
      if (elm.getId() == id) {
        return elm;
      }
    }

    return null;
  }

  moveToNewGroup(currentItem, newGroup: Group) {
    let currentGroup: Group = this.getGroupById(currentItem.config.getGroupId());
    currentGroup.removeItem(currentItem);
    newGroup.addItem(currentItem);
    this._groupItemMoved$.next(currentItem);
    this._groupListUpdated$.next(this._groupList);
  }

  getEnvironmentConfig() {
    // hiddenGroups: Array.from(this.hiddenGroups.keys()).sort(),
    return {  labelsVisibility: this.getLabelsVisibility() };
  }

  getLabelsVisibility():{id:number, visibility:boolean}[]{
    return this._groupList.filter(g => !g.is_empty).map((g:Group)=>{
      return {id: g.getId(), visibility: g.labelVisibility}
    })
  }

  setEnvironmentConfig(config, active = true) {
    // console.log('setEnvironmentConfig', config);
    // if (config.hasOwnProperty('hiddenGroups') && Array.isArray(config.hiddenGroups)) {
    //   for (const group of this._groupList) {
    //     if (config.hiddenGroups.includes(group.getId())) {
    //       this.setGroupItemsVisibility(group, false);
    //     } else {
    //       this.setGroupItemsVisibility(group, true);
    //     }
    //   }
    // }
  }

  setGroupLabelsVisibilityByEnvironmentConfig(groupLabelsVisible: {id:number, visibility:boolean}[]) {
    groupLabelsVisible.forEach((_item: {id:number, visibility:boolean})=>{
      let group = this.getGroupById(_item.id);
      if (group){
        this.setGroupLabelsVisibility(group, _item.visibility);
      }
    })
  }
  setGroupLabelsVisibility(item:Group, visibility?){
    if (visibility == undefined){
      visibility = item.labelVisibility;
    }
    item.labelVisibility = visibility;
    item.items.forEach((__item)=>{
      __item.toggleLabelVisibility(item.labelVisibility);
    })
    if (item.subgroups){
      item.subgroups.forEach((__subgroup)=>{
        this.setGroupLabelsVisibility(__subgroup, visibility);
      })
    }
  }
}
